import React, { useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  Button,
  CardActionArea,
  Chip
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import PlayCircleFilledRoundedIcon from '@mui/icons-material/PlayCircleFilledRounded';
import { Link as RouterLink } from 'react-router-dom';

const tutorialPlaylist = [
  {
    title: 'Getting started',
    duration: '9:32',
    description: 'Shows how to create an account, log in, and start a new project.',
    url: 'https://youtu.be/P_-U0VkrEXc',
    videoId: 'P_-U0VkrEXc'
  },
  {
    title: 'Luminara DOOD + Scheduling walkthrough',
    duration: 'New',
    description: 'See how to set up a cast member, link them to the OneLiner, and have Day Out of Days reflect the updates instantly.',
    url: 'https://youtu.be/UIKM75ibrT4',
    videoId: 'UIKM75ibrT4'
  },
  {
    title: 'Logline generator',
    duration: '4:10',
    description: 'Walkthrough of generating strong loglines with the AI assistant.',
    url: 'https://youtu.be/uvk35Awaisk',
    videoId: 'uvk35Awaisk'
  },
  {
    title: 'Creating characters',
    duration: '5:05',
    description: 'Build richer characters fast: core traits, backstory, motivations, flaws, and exportable profiles.',
    url: 'https://youtu.be/Z_5o0iyqhd8',
    videoId: 'Z_5o0iyqhd8'
  },
  {
    title: 'Upload your script & see the timeline',
    duration: '3:40',
    description: 'Upload your screenplay, auto-tag characters/scenes/locations, and open the timeline to view flow and pacing.',
    url: 'https://youtu.be/oPxt7qmN_cQ',
    videoId: 'oPxt7qmN_cQ'
  },
  {
    title: 'Onboarding walkthrough',
    duration: '6:45',
    description: 'Set up your first production, invite teammates, and configure roles.',
    url: 'https://youtu.be/98U3oV6h7KM',
    videoId: '98U3oV6h7KM'
  },
  {
    title: 'Scheduling essentials',
    duration: '8:12',
    description: 'Build call sheets, shooting schedules, and milestone timelines with ease.',
    status: 'Coming soon'
  },
  {
    title: 'Asset library tour',
    duration: '5:30',
    description: 'Organize concept art, storyboards, and references for your crew in one hub.',
    status: 'Coming soon'
  }
];

function Tutorials() {
  const theme = useTheme();

  useEffect(() => {
    document.title = 'Tutorials • Luminara';
  }, []);

  const featured = useMemo(() => tutorialPlaylist.find((t) => t.url), []);

  const getThumbnail = (item) => {
    if (item.videoId) {
      return `https://img.youtube.com/vi/${item.videoId}/hqdefault.jpg`;
    }
    // Fallback gradient placeholder
    return null;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `radial-gradient(circle at 20% 20%, ${alpha(theme.palette.primary.light, 0.12)}, transparent 35%),
          radial-gradient(circle at 80% 0%, ${alpha(theme.palette.secondary.light, 0.16)}, transparent 30%),
          ${theme.palette.background.default}`,
        py: { xs: 6, md: 10 }
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={{ xs: 6, md: 8 }}>
          <Stack spacing={2} textAlign="center" alignItems="center">
            <Chip label="Tutorials" color="primary" variant="outlined" sx={{ fontWeight: 700, letterSpacing: 1 }} />
            <Typography variant="h3" fontWeight={800}>
              Learn Luminara, step by step
            </Typography>
            <Typography variant="body1" color="text.secondary" maxWidth={720}>
              Watch walkthroughs covering setup, scheduling, collaboration, and AI story tools. We’ll keep adding fresh episodes as new features land.
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" justifyContent="center">
              <Button
                component={RouterLink}
                to="/register"
                variant="contained"
                size="large"
                startIcon={<PlayCircleFilledRoundedIcon />}
                sx={{ borderRadius: 999 }}
              >
                Start a project
              </Button>
              <Button
                component={RouterLink}
                to="/"
                variant="outlined"
                size="large"
                sx={{ borderRadius: 999 }}
              >
                Back to home
              </Button>
            </Stack>
          </Stack>

          {featured && (
            <Card
              elevation={0}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 4,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.secondary.main, 0.14)})`,
                boxShadow: `0 14px 40px ${alpha(theme.palette.common.black, 0.25)}`
              }}
            >
              <Grid container>
                <Grid item xs={12} md={7}>
                  <CardActionArea
                    component="a"
                    href={featured.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ height: '100%' }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        minHeight: { xs: 220, md: 320 },
                        backgroundColor: alpha(theme.palette.common.black, 0.16),
                        backgroundImage: getThumbnail(featured)
                          ? `linear-gradient(180deg, ${alpha(theme.palette.common.black, 0.35)} 0%, ${alpha(theme.palette.common.black, 0.55)} 100%), url(${getThumbnail(featured)})`
                          : `radial-gradient(circle at 20% 20%, ${alpha(theme.palette.primary.main, 0.25)}, transparent 45%),
                            radial-gradient(circle at 80% 0%, ${alpha(theme.palette.secondary.main, 0.25)}, transparent 40%)`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Stack spacing={1} alignItems="center" sx={{ color: '#fff' }}>
                        <Box
                          sx={{
                            width: 72,
                            height: 72,
                            borderRadius: '50%',
                            display: 'grid',
                            placeItems: 'center',
                            backgroundColor: alpha(theme.palette.common.white, 0.25),
                            backdropFilter: 'blur(6px)',
                            border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`
                          }}
                        >
                          <PlayCircleFilledRoundedIcon sx={{ fontSize: 48, color: '#fff' }} />
                        </Box>
                        <Typography variant="subtitle2" sx={{ letterSpacing: 0.5, opacity: 0.9 }}>
                          {featured.duration} • Watch on YouTube
                        </Typography>
                      </Stack>
                    </Box>
                  </CardActionArea>
                </Grid>
                <Grid item xs={12} md={5}>
                  <CardContent sx={{ p: { xs: 3, md: 4 }, height: '100%', display: 'flex', alignItems: 'center' }}>
                    <Stack spacing={2.5}>
                      <Stack spacing={1}>
                        <Chip label="Featured" color="secondary" size="small" sx={{ alignSelf: 'flex-start' }} />
                        <Typography variant="h5" fontWeight={800}>
                          {featured.title}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {featured.description}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={2}>
                        <Button
                          component="a"
                          href={featured.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="contained"
                          startIcon={<PlayCircleFilledRoundedIcon />}
                          sx={{ borderRadius: 999 }}
                        >
                          Watch tutorial
                        </Button>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Grid>
              </Grid>
            </Card>
          )}

          <Grid container spacing={3}>
            {tutorialPlaylist
              .filter((item) => item.title !== featured?.title)
              .map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.title}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    backgroundColor: alpha(theme.palette.background.paper, 0.96),
                    overflow: 'hidden'
                  }}
                >
                  <CardActionArea
                    component={item.url ? 'a' : 'div'}
                    href={item.url || undefined}
                    target={item.url ? '_blank' : undefined}
                    rel={item.url ? 'noopener noreferrer' : undefined}
                    disabled={!item.url}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        pt: '56.25%',
                        background: getThumbnail(item)
                          ? `linear-gradient(180deg, ${alpha(theme.palette.common.black, 0.2)} 0%, ${alpha(theme.palette.common.black, 0.5)} 100%), url(${getThumbnail(item)})`
                          : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.16)} 0%, ${alpha(theme.palette.secondary.main, 0.12)} 100%)`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 12,
                          left: 12,
                          display: 'flex',
                          gap: 1,
                          alignItems: 'center'
                        }}
                      >
                        <Chip
                          label={item.duration || 'Coming soon'}
                          size="small"
                          sx={{
                            bgcolor: alpha(theme.palette.common.black, 0.55),
                            color: '#fff',
                            borderColor: alpha(theme.palette.common.white, 0.4),
                            border: '1px solid'
                          }}
                        />
                        {!item.url && (
                          <Chip
                            label="Coming soon"
                            size="small"
                            color="default"
                            sx={{ bgcolor: alpha(theme.palette.background.default, 0.8) }}
                          />
                        )}
                      </Box>
                      {item.url && (
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'grid',
                            placeItems: 'center',
                            color: '#fff'
                          }}
                        >
                          <Box
                            sx={{
                              width: 56,
                              height: 56,
                              borderRadius: '50%',
                              display: 'grid',
                              placeItems: 'center',
                              backgroundColor: alpha(theme.palette.common.black, 0.35),
                              border: `1px solid ${alpha(theme.palette.common.white, 0.35)}`
                            }}
                          >
                            <PlayCircleFilledRoundedIcon sx={{ fontSize: 38 }} />
                          </Box>
                        </Box>
                      )}
                    </Box>
                    <CardContent sx={{ p: 3 }}>
                      <Stack spacing={1.5}>
                        <Typography variant="h6" fontWeight={700}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.description}
                        </Typography>
                        {item.url ? (
                          <Button
                            component="a"
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="contained"
                            size="small"
                            startIcon={<PlayCircleFilledRoundedIcon />}
                            sx={{ alignSelf: 'flex-start', borderRadius: 999 }}
                          >
                            Watch tutorial
                          </Button>
                        ) : (
                          <Typography variant="caption" sx={{ fontWeight: 600, color: alpha(theme.palette.text.primary, 0.65) }}>
                            {item.status}
                          </Typography>
                        )}
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Stack>
      </Container>
    </Box>
  );
}

export default Tutorials;
